/* eslint-disable @typescript-eslint/no-explicit-any */
import { path } from 'ramda';
import type { styleFn } from 'styled-system';

import { rgba } from '@utils/dom.ts';

/**
 * Builds a `shouldForwardProp` that stops styled-system props (`bg`, `p`, `flexDirection`, ...) from
 * reaching the DOM. Pass the same `compose(...)` parser the component uses for its styles so the
 * filter can't drift from the props that are actually consumed:
 *
 *   const system = compose(color, space);
 *   const Thing = styled('div').withConfig({ shouldForwardProp: createSystemPropFilter(system) })`${system}`;
 *
 * `keep` lists system props that must still be forwarded because the element needs them as real
 * attributes too (e.g. `width`/`height` on an `<svg>`).
 *
 * Use this for components whose public API is styled-system props. For the app's own one-off props,
 * use a `$`-prefixed transient prop instead.
 */
export const createSystemPropFilter = (system: styleFn, keep: readonly string[] = []) => {
  const consumed = new Set((system.propNames ?? []).filter(name => !keep.includes(name)));
  return (prop: string) => !consumed.has(prop);
};

export const getThemeVal =
  (string: string, defaultValue?: any) =>
  ({ theme }: any) =>
    path(string.split('.'), theme) || defaultValue;

export const getThemeColor =
  (color: string, defaultColor?: string, rgbaVal = 1) =>
  ({ theme }: any) =>
    rgba(`${theme.colors[color]}`, rgbaVal) || defaultColor;

/**
 * Returns a CSS variable reference for a color, optionally with alpha transparency
 * @param {string} color - The color name (e.g., 'primary', 'widgetText')
 * @param {string} defaultColor - Fallback color if CSS variable not available
 * @param {number} alpha - Optional alpha value (0-1). If provided, uses pre-generated alpha variant.
 * @returns {string} CSS variable reference like 'var(--color-primary)' or 'var(--color-primary-50)'
 *
 * Examples:
 *   getCSSColor('primary') => 'var(--color-primary)'
 *   getCSSColor('primary', '#2266DD', 0.5) => 'var(--color-primary-50)'
 *   getCSSColor('widgetText', '#333333', 0.1) => 'var(--color-widgetText-10)'
 */
export const getCSSColor = (color: string, defaultColor?: string, alpha = 1) => {
  if (alpha === 1) {
    return `var(--color-${color}, ${defaultColor || 'currentColor'})`;
  }
  // Convert alpha to percentage (0.1 => 10, 0.5 => 50, etc.)
  const alphaPercent = Math.round(alpha * 100);
  const varName = `--color-${color}-${alphaPercent.toString().padStart(2, '0')}`;
  const cssVar = `var(${varName}, ${defaultColor || 'currentColor'})`;

  // In debug mode, warn if CSS variable doesn't exist
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    // Check on next tick to ensure styles are loaded
    setTimeout(() => {
      const style = getComputedStyle(document.documentElement);
      const value = style.getPropertyValue(varName);
      if (!value) {
        console.warn(`CSS variable "${varName}" is not defined. Using fallback: ${defaultColor || 'currentColor'}`);
      }
    }, 0);
  }

  return cssVar;
};
